// ============================================================
// Die Wege, die mehr als eine Stelle kennen muss (SPEC §13.5).
//
// Die Pfade der Bildschirme stehen in App.tsx; hier stehen die Bausteine, die
// Kopfleiste, Mobilleiste und Anmeldung teilen: welcher Reiter leuchtet, und wohin
// es nach der Anmeldung geht.
// ============================================================
import { matchPath } from "react-router-dom";

/** Wege zum Katalog, zum Modell und zu den Ständen (§13.5) — gebaut statt getippt. */
export const weg = {
  kataloge: () => "/",
  // Mit Projekt (§19.17-1): „Katalog anlegen" an einem Projekt ohne Katalog wählt es gleich vor.
  neuerKatalog: (projektId?: string) => (projektId ? `/kataloge/neu?projekt=${encodeURIComponent(projektId)}` : "/kataloge/neu"),
  geloescht: () => "/kataloge/geloescht",
  editor: (katalogId: string) => `/k/${katalogId}`,
  staende: (katalogId: string) => `/k/${katalogId}/staende`,
  stand: (katalogId: string, standId: string) => `/k/${katalogId}/staende/${standId}`,
  modell: (projektId: string) => `/p/${projektId}/modell`,
  // Mit Katalog (§13.5): „Zuordnen" aus dem Editor heraus kennt den Katalog, in dem die
  // Karten entstehen sollen (§9.6, zuordnung_karte).
  zuordnen: (projektId: string, katalogId?: string) =>
    katalogId ? `/p/${projektId}/zuordnen?katalog=${encodeURIComponent(katalogId)}` : `/p/${projektId}/zuordnen`,
  importe: (projektId: string) => `/p/${projektId}/importe`,
  bericht: (projektId: string, importId: string) => `/p/${projektId}/importe/${importId}`,
  bibliothek: () => "/bibliothek",
  produkt: (produktId: string) => `/bibliothek/${produktId}`,
  profil: () => "/profil",
  admin: (bereich: "nutzer" | "bibliothek" | "kategorien" | "regeln" | "ernte" | "maschinen" = "nutzer") => `/admin/${bereich}`,
};

/**
 * Leuchtet ein Reiter der Kopf- oder Mobilleiste? „Kataloge" (`/`) gilt auch im
 * Editor (`/k/…`), beim Anlegen und im Modell eines Projekts (`/p/…`) — dort arbeitet
 * man an einem Katalog, nicht in der Bibliothek. Sonst: Präfix an einer
 * Abschnittsgrenze, mit `genau` nur der Pfad selbst.
 */
export function reiterAn(zu: string, pfad: string, genau = false): boolean {
  if (zu === "/") {
    return pfad === "/" || pfad.startsWith("/k/") || pfad.startsWith("/kataloge") || pfad.startsWith("/p/");
  }
  return matchPath({ path: zu, end: genau }, pfad) !== null;
}

/**
 * Wohin nach der Anmeldung? `von` setzen die Wächter in `App.tsx` (Pfad + Suche). Es
 * kommt aus `history.state` und ist damit Eingabe. Durch kommt nur ein Pfad DIESER
 * Anwendung (Kopie aus lilasprojekte lib/wege.ts):
 *  - er beginnt mit „/", aber nicht mit „//" (ein anderer Rechner);
 *  - kein Backslash — Browser lesen `/\fremd.de` wie `//fremd.de`;
 *  - keine Steuerzeichen — aus `/\t/fremd.de` würde `//fremd.de`;
 *  - nicht die Anmeldung selbst, sonst liefe man nach dem Code wieder dorthin.
 */
export function anmeldeZiel(von: unknown): string {
  if (typeof von !== "string") return "/";
  if (!von.startsWith("/") || von.startsWith("//")) return "/";
  // eslint-disable-next-line no-control-regex -- genau die Steuerzeichen sind gemeint
  if (/[\\\u0000-\u001f\u007f]/.test(von)) return "/";
  const pfad = von.split(/[?#]/, 1)[0] ?? "";
  let gelesen: string;
  try {
    gelesen = decodeURIComponent(pfad).toLowerCase();
  } catch {
    return "/";
  }
  if (gelesen.startsWith("//") || gelesen.includes("\\") || gelesen.startsWith("/anmelden")) return "/";
  return von;
}
